// Coffre-fort documentaire : les fichiers vivent dans le stockage objet du backend,
// la base partagée ne garde que le lien. Utilisé par tous les écrans qui déposent un fichier.
const API = `${process.env.REACT_APP_BACKEND_URL}/api/docs`;

export const urlDocument = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/docs/${id}/fichier`;

export async function deposerDocument(fichier, { source = 'coffre', categorie = '', deposePar = '', lien = null, indexer = true } = {}) {
  const body = new FormData();
  body.append('fichier', fichier);
  body.append('source', source);
  body.append('categorie', categorie ?? '');
  body.append('deposePar', deposePar ?? '');
  body.append('indexer', indexer ? 'true' : 'false');
  if (lien) body.append('lien', JSON.stringify(lien));
  let r;
  try {
    r = await fetch(`${API}/upload`, { method: 'POST', body });
  } catch (reseau) {
    return { error: `Dépôt impossible — serveur injoignable (${reseau?.message ?? 'réseau'}).` };
  }
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    // L'erreur réelle est remontée telle quelle : un code de passerelle (413, 502,
    // 504…) ne doit pas être confondu avec un refus applicatif ni masqué.
    const detail = data?.detail || `réponse ${r.status}${r.statusText ? ` ${r.statusText}` : ''} sans détail serveur`;
    return { error: `Dépôt refusé — ${detail}`, statut: r.status };
  }
  const doc = data?.document;
  if (!doc) return { error: `Dépôt incomplet — le serveur a répondu ${r.status} sans fiche de document.` };
  return { error: null, document: doc, doublon: data.doublon, url: urlDocument(doc.id) };
}

export async function analyseProfonde(id, par = '') {
  const r = await fetch(`${API}/${id}/analyse-profonde`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ par }),
  });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null, ...data } : { error: data.detail || 'Analyse profonde impossible.' };
}

export async function fetchKpisCampagnes() {
  const r = await fetch(`${API}/kpis`);
  return r.ok ? (await r.json()).kpis ?? [] : [];
}

export async function majKpiCampagne(id, patch) {
  const r = await fetch(`${API}/kpis/${id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null, ...data } : { error: data.detail || 'Mise à jour impossible.' };
}

export async function supprimerKpiCampagne(id) {
  const r = await fetch(`${API}/kpis/${id}`, { method: 'DELETE' });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null } : { error: data.detail || 'Suppression impossible.' };
}

export async function fetchDocuments({ corbeille = false, source = '', categorie = '', annee = '' } = {}) {
  const q = new URLSearchParams({ corbeille: String(corbeille), source, categorie, annee });
  const r = await fetch(`${API}?${q}`);
  if (!r.ok) return { documents: [], miroirEnAttente: 0, categories: [] };
  return r.json();
}

export async function rechercherDocuments(question) {
  const r = await fetch(`${API}/recherche`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { error: data.detail || 'Recherche impossible.', documents: [] };
  return { error: null, ...data };
}

export async function analyserLot(fichier, { appliquer = false, deposePar = '' } = {}) {
  const body = new FormData();
  body.append('fichier', fichier);
  body.append('appliquer', appliquer ? 'true' : 'false');
  body.append('deposePar', deposePar ?? '');
  const r = await fetch(`${API}/split`, { method: 'POST', body });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) return { error: data.detail || 'Découpage impossible.' };
  return { error: null, ...data };
}

export async function mettreALaCorbeille(id, par) {
  const r = await fetch(`${API}/${id}/corbeille`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ par }),
  });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null, ...data } : { error: data.detail || 'Suppression impossible.' };
}

export async function restaurerDocument(id) {
  const r = await fetch(`${API}/${id}/restaurer`, { method: 'POST' });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null } : { error: data.detail || 'Restauration impossible.' };
}

export async function migrerArchives() {
  const r = await fetch(`${API}/migration`, { method: 'POST' });
  const data = await r.json().catch(() => ({}));
  return r.ok ? { error: null, ...data } : { error: data.detail || 'Migration impossible.' };
}

export async function fileMiroir() {
  const r = await fetch(`${API}/miroir/file`);
  if (r.ok) return r.json();
  // État illisible ≠ Drive absent : l'écran le dit au lieu d'afficher « non connecté ».
  return {
    cible: 'google_drive',
    identifiants: false,
    configure: false,
    enAttente: 0,
    fichiers: [],
    erreurEtat: `état du miroir illisible (réponse ${r.status})`,
  };
}

export const SOURCES = {
  coffre: { label: 'Dépôt libre', couleur: 'bg-slate-100 text-slate-700 border-slate-300' },
  coffre_split: { label: 'Lot découpé', couleur: 'bg-violet-50 text-violet-800 border-violet-300' },
  facture_frais: { label: 'Facture de frais', couleur: 'bg-amber-50 text-amber-800 border-amber-300' },
  scan_mobile: { label: 'Scan mobile', couleur: 'bg-sky-50 text-sky-800 border-sky-300' },
  recu_coursier: { label: 'Reçu coursier', couleur: 'bg-emerald-50 text-emerald-800 border-emerald-300' },
  justificatif_caisse: { label: 'Justificatif caisse', couleur: 'bg-indigo-50 text-indigo-800 border-indigo-300' },
  releve_bancaire: { label: 'Relevé bancaire', couleur: 'bg-cyan-50 text-cyan-800 border-cyan-300' },
  amortissement: { label: 'Tableau d’amortissement', couleur: 'bg-rose-50 text-rose-800 border-rose-300' },
  migration: { label: 'Archive migrée', couleur: 'bg-slate-100 text-slate-600 border-slate-300' },
};

// Écran d'origine d'un document, pour le lien « voir l'écriture ».
export const LIENS = {
  facture_frais: '/frais-generaux',
  scan_mobile: '/frais-generaux',
  recu_coursier: '/validations',
  justificatif_caisse: '/finances',
  releve_bancaire: '/finances',
  amortissement: '/tresorerie',
};
